// バッチコード生成コンポーネント

import { createAppSection } from './app-importer.js';
import { t, getSettingCode, getSettingRevertCode, getSettingTitle } from '../utils/i18n.js';
import type { ImportedApp, SettingsData, SettingItem, NICConfig, NICConfigStorage } from '../types/index.js';

/**
 * 変数の値を取得（localStorageから直接取得）
 * @param itemId - 項目ID
 * @param varName - 変数名
 * @param defaultValue - デフォルト値
 * @returns 変数の値
 */
function getVariableValue(itemId: string, varName: string, defaultValue = ''): string {
    if (typeof window === 'undefined' || !window.localStorage) return defaultValue;
    
    try {
        const raw = window.localStorage.getItem('winbatchgen_variables_v1');
        if (!raw) return defaultValue;
        
        const variables: Record<string, Record<string, string>> = JSON.parse(raw);
        if (variables[itemId] && variables[itemId][varName] !== undefined) {
            return variables[itemId][varName];
        }
    } catch (error) {
        console.error('変数の取得エラー:', error);
    }
    
    return defaultValue;
}

/**
 * コード内の変数を置換
 * @param code - 元のコード
 * @param item - 設定項目
 * @returns 置換後のコード
 */
function replaceVariables(code: string, item: SettingItem): string {
    if (!item.variables || !code) return code;
    
    let replacedCode = code;
    item.variables.forEach(variable => {
        const value = getVariableValue(item.id, variable.name, variable.defaultValue || '');
        // {{変数名}} を値で置換
        const regex = new RegExp(`\\{\\{${variable.name}\\}\\}`, 'g');
        replacedCode = replacedCode.replace(regex, value);
    });
    
    return replacedCode;
}

/**
 * NIC設定のコードを生成
 * @param itemId - 項目ID
 * @param logEnabled - ログ有効か
 * @param logCode - ログリダイレクトコード
 * @returns 生成されたコード
 */
function generateNICCode(itemId: string, logEnabled: boolean, logCode: string): string {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    
    try {
        const raw = window.localStorage.getItem('winbatchgen_nic_config_v1');
        if (!raw) return '';
        
        const config: NICConfigStorage = JSON.parse(raw);
        const nics = config[itemId] || [];
        
        if (nics.length === 0) return '';
        
        let code = '';
        nics.forEach((nic: NICConfig, index: number) => {
            code += `:: NIC #${index + 1} (${nic.adapterName})\n`;
            if (nic.configType === 'static') {
                // ゲートウェイが空の場合はゲートウェイなしで設定
                if (nic.gateway && nic.gateway.trim()) {
                    code += `netsh interface ip set address "${nic.adapterName}" static ${nic.ipAddress} ${nic.subnetMask} ${nic.gateway} 1\n`;
                } else {
                    code += `netsh interface ip set address "${nic.adapterName}" static ${nic.ipAddress} ${nic.subnetMask}\n`;
                }
                // DNSが空でない場合のみ設定
                const dns1 = nic.dnsPrimary || nic.dns1 || '';
                const dns2 = nic.dnsSecondary || nic.dns2 || '';
                if (dns1 && dns1.trim()) {
                    code += `netsh interface ip set dns "${nic.adapterName}" static ${dns1}\n`;
                    if (dns2 && dns2.trim()) {
                        code += `netsh interface ip add dns "${nic.adapterName}" ${dns2} index=2\n`;
                    }
                }
            } else {
                code += `netsh interface ip set dns "${nic.adapterName}" dhcp\n`;
                code += `netsh interface ip set address "${nic.adapterName}" dhcp\n`;
            }
            code += '\n';
        });
        
        if (logEnabled) {
            // ログ出力を追加（echo文にログリダイレクトを追加）
            code = code.replace(/(echo[^\n]*)/g, '$1' + logCode);
        }
        
        return code;
    } catch (error) {
        console.error('NIC設定のコード生成エラー:', error);
        return '';
    }
}

/**
 * バッチファイルのコードを生成
 * @param importedApps - インポートされたアプリケーションリスト
 * @param settings - 設定データ（未指定の場合は空設定として扱う）
 * @returns 生成されたバッチコード
 */
export function generateBatchCode(importedApps: ImportedApp[] = [], settings?: SettingsData): string {
    let code = "@echo off\nsetlocal enabledelayedexpansion\n\n";
    code += ":: Windows Setup Batch Script\n";
    code += ":: Generated by Win Batch Gen\n\n";
    
    // ログ機能のチェック
    const logEnabled = (document.getElementById('app_log') as HTMLInputElement | null)?.checked || false;
    let logCode = '';
    if (logEnabled) {
        code += `:: ${t('batchLogConfig')}\n`;
        code += "set \"ROOT=%~dp0\"\n";
        code += "if not exist \"%ROOT%logs\" mkdir \"%ROOT%logs\"\n";
        code += "for /f \"tokens=1-3 delims=/ \" %%a in (\"%DATE%\") do set YY=%%c& set MM=%%a& set DD=%%b\n";
        code += "set \"LOG=%ROOT%logs\\setup_%COMPUTERNAME%_%YY%%MM%%DD%.log\"\n";
        code += "echo %DATE% %TIME% >> \"%LOG%\"\n\n";
        logCode = " >> \"%LOG%\"";
    }
    
    code += "echo ========================================\n";
    code += `echo        ${t('batchSetupStart')}\n`;
    code += "echo ========================================\n";
    if (logEnabled) {
        code += "echo %DATE% %TIME%" + logCode + "\n";
    }
    code += "\n";
    
    // 管理者権限が必要な設定があるかチェック
    let needsAdmin = false;
    const sections = settings?.sections || [];
    sections.forEach(section => {
        section.items.forEach(item => {
            if (item.requiresAdmin && (document.getElementById(item.id) as HTMLInputElement | null)?.checked) {
                needsAdmin = true;
            }
        });
    });

    // アプリケーションセクションを追加
    const appSection = createAppSection(importedApps);

    // アプリケーションインストールがある場合も管理者権限が必要
    const appSections = appSection?.sections;
    const firstAppSection = appSections?.[0];
    if (firstAppSection && firstAppSection.items.length > 0) {
        needsAdmin = true;
    }
    
    // インストーラーパスの設定
    let installerPath = 'installer';
    if (importedApps.length > 0 && importedApps[0]?.path) {
        installerPath = importedApps[0]!.path;
        // パスからルート部分を除去して相対パスに
        if (installerPath.includes('\\')) {
            const parts = installerPath.split('\\');
            const installerIndex = parts.findIndex(p => p.toLowerCase().includes('installer'));
            if (installerIndex >= 0) {
                installerPath = parts.slice(installerIndex).join('\\');
            }
        }
    }
    
    if (!logEnabled) {
        code += "set \"ROOT=%~dp0\"\n";
    }
    code += "set \"INSTALLER=%ROOT%" + installerPath + "\"\n";
    code += "\n";
    
    // 管理者権限チェック（必要に応じて自動追加、またはユーザーが明示的に有効にした場合）
    const adminToggle = document.getElementById('toggleAdmin') as HTMLInputElement | null;
    const shouldAddAdminCheck = needsAdmin || adminToggle?.checked || false;
    
    if (shouldAddAdminCheck) {
        code += `:: ${t('batchAdminSelfElevate')}\n`;
        code += "net session >nul 2>&1\n";
        code += "if errorlevel 1 (\n";
        code += "  set \"IS_ELEVATED=0\"\n";
        code += ") else (\n";
        code += "  set \"IS_ELEVATED=1\"\n";
        code += ")\n\n";
        code += "if \"%IS_ELEVATED%\"==\"0\" (\n";
        code += `  echo ${t('batchAdminNotElevated')}\n`;
        code += "  powershell -NoProfile -ExecutionPolicy Bypass -Command \"Start-Process -FilePath '%~f0' -Verb RunAs\"\n";
        code += "  exit /b 0\n";
        code += ") else (\n";
        code += `  echo ${t('batchAdminElevated')}\n`;
        code += ")\n\n";
        code += `:: ${t('batchAdminCheck')}\n`;
        code += `echo ${t('batchAdminChecking')}\n`;
        code += "if \"%IS_ELEVATED%\"==\"1\" (\n";
        code += `  echo ${t('batchAdminConfirmed')}\n`;
        code += ") else (\n";
        code += "  echo ========================================\n";
        code += `  echo        ${t('batchAdminRequired')}\n`;
        code += "  echo ========================================\n";
        code += "  pause\n";
        code += "  exit /b 1\n";
        code += ")\n\n";
    }
    
    // アプリケーションインストールセクション
    if (firstAppSection && firstAppSection.items.length > 0) {
        const hasSelectedApps = firstAppSection.items.some(item => (document.getElementById(item.id) as HTMLInputElement | null)?.checked);
        if (hasSelectedApps) {
            code += "echo.\n";
            code += "echo ========================================\n";
            code += `echo        ${t('batchApplicationInstall')}\n`;
            code += "echo ========================================\n";
            code += "\n";
            
            firstAppSection.items.forEach(item => {
                if ((document.getElementById(item.id) as HTMLInputElement | null)?.checked) {
                    let appCode = item.code || '';
                    if (logEnabled) {
                        // ログ出力を追加（echo文にログリダイレクトを追加）
                        appCode = appCode.replace(/(echo [^\n]*)/g, '$1' + logCode);
                    }
                    code += appCode;
                }
            });
            code += "\n";
        }
    }
    
    // 各設定項目のコードを生成（ヘッダーオプションとフッターオプションを除外）
    sections.forEach(section => {
        section.items.forEach(item => {
            if ((item as any).isAdminCheck || (item as any).isHeaderOption || (item as any).isFooterOption || (item as any).isAppInstall) return;
            if ((document.getElementById(item.id) as HTMLInputElement | null)?.checked) {
                code += "echo.\n";
                code += "echo ========================================\n";
                // 翻訳対応
                code += `echo        ${getSettingTitle(item.id, item.title)}\n`;
                code += "echo ========================================\n";
                
                // NIC設定の場合は特別な処理
                if ((item as any).isDynamicList && (item as any).listItemType === 'nic') {
                    const nicCode = generateNICCode(item.id, logEnabled, logCode);
                    code += nicCode;
                } else {
                    // 翻訳されたコードを取得（なければ元のコードを使用）
                    const itemCodeRaw = getSettingCode(item.id, item.code || '');
                    if (logEnabled && itemCodeRaw) {
                        // 変数を置換
                        let itemCode = replaceVariables(itemCodeRaw, item);
                        // ログ出力を追加（echo文にログリダイレクトを追加）
                        const codeWithLog = itemCode.replace(/(echo[^\n]*)/g, '$1' + logCode);
                        code += codeWithLog;
                    } else {
                        // 変数を置換
                        let itemCode = replaceVariables(itemCodeRaw, item);
                        code += itemCode;
                    }
                }
                code += "\n";
            }
        });
    });

    // フッターオプション（エクスプローラー再起動、再起動など）
    const explorerRestart = (document.getElementById('exec_explorer_restart') as HTMLInputElement | null)?.checked || false;
    const reboot = (document.getElementById('exec_reboot') as HTMLInputElement | null)?.checked || false;
    
    if (explorerRestart) {
        code += "echo.\n";
        code += `echo ${t('batchApplyingSettings')}\n`;
        const restartItem = sections
            .flatMap(s => s.items)
            .find(item => item.id === 'exec_explorer_restart');
        if (restartItem) {
            const restartCode = getSettingCode(restartItem.id, restartItem.code || '');
            code += restartCode || '';
        }
    }
    
    if (reboot) {
        code += "\n";
        const rebootItem = sections
            .flatMap(s => s.items)
            .find(item => item.id === 'exec_reboot');
        if (rebootItem) {
            const rebootCode = getSettingCode(rebootItem.id, rebootItem.code || '');
            code += rebootCode || '';
        }
    } else {
        code += "\necho.\n";
        code += "echo ========================================\n";
        code += `echo        ${t('batchSetupComplete')}\n`;
        code += "echo ========================================\n";
        if (logEnabled) {
            code += `echo ${t('batchLogFile')}\n`;
        }
        code += "pause\n";
    }
    
    return code;
}

/**
 * 元に戻すバッチファイルのコードを生成
 * @param importedApps - インポートされたアプリケーションリスト
 * @param settings - 設定データ
 * @returns 生成されたバッチコード
 */
export function generateRevertBatchCode(_importedApps: ImportedApp[] = [], settings: SettingsData): string {
    let code = "@echo off\nsetlocal enabledelayedexpansion\n\n";
    code += ":: Windows Setup Revert Batch Script\n";
    code += ":: Generated by Win Batch Gen (Revert)\n\n";
    
    // ログ機能のチェック
    const logEnabled = (document.getElementById('app_log') as HTMLInputElement | null)?.checked || false;
    let logCode = '';
    if (logEnabled) {
        code += `:: ${t('batchLogConfig')}\n`;
        code += "set \"ROOT=%~dp0\"\n";
        code += "if not exist \"%ROOT%logs\" mkdir \"%ROOT%logs\"\n";
        code += "for /f \"tokens=1-3 delims=/ \" %%a in (\"%DATE%\") do set YY=%%c& set MM=%%a& set DD=%%b\n";
        code += "set \"LOG=%ROOT%logs\\revert_%COMPUTERNAME%_%YY%%MM%%DD%.log\"\n";
        code += "echo %DATE% %TIME% >> \"%LOG%\"\n\n";
        logCode = " >> \"%LOG%\"";
    }
    
    code += "echo ========================================\n";
    code += `echo        ${t('batchRevertSettings')}\n`;
    code += "echo ========================================\n";
    if (logEnabled) {
        code += "echo %DATE% %TIME%" + logCode + "\n";
    }
    code += "\n";
    
    // 管理者権限が必要な設定があるかチェック
    let needsAdmin = false;
    const sections = settings.sections || [];
    sections.forEach(section => {
        section.items.forEach(item => {
            if (item.requiresAdmin && (document.getElementById(item.id) as HTMLInputElement | null)?.checked && item.revertCode) {
                needsAdmin = true;
            }
        });
    });
    
    // 管理者権限チェック（必要に応じて自動追加）
    const adminToggle = document.getElementById('toggleAdmin') as HTMLInputElement | null;
    const shouldAddAdminCheck = needsAdmin || adminToggle?.checked || false;
    
    if (shouldAddAdminCheck) {
        code += `:: ${t('batchAdminSelfElevate')}\n`;
        code += "net session >nul 2>&1\n";
        code += "if errorlevel 1 (\n";
        code += "  set \"IS_ELEVATED=0\"\n";
        code += ") else (\n";
        code += "  set \"IS_ELEVATED=1\"\n";
        code += ")\n\n";
        code += "if \"%IS_ELEVATED%\"==\"0\" (\n";
        code += `  echo ${t('batchAdminNotElevated')}\n`;
        code += "  powershell -NoProfile -ExecutionPolicy Bypass -Command \"Start-Process -FilePath '%~f0' -Verb RunAs\"\n";
        code += "  exit /b 0\n";
        code += ") else (\n";
        code += `  echo ${t('batchAdminElevated')}\n`;
        code += ")\n\n";
        code += `:: ${t('batchAdminCheck')}\n`;
        code += `echo ${t('batchAdminChecking')}\n`;
        code += "if \"%IS_ELEVATED%\"==\"1\" (\n";
        code += `  echo ${t('batchAdminConfirmed')}\n`;
        code += ") else (\n";
        code += "  echo ========================================\n";
        code += `  echo        ${t('batchAdminRequired')}\n`;
        code += "  echo ========================================\n";
        code += "  pause\n";
        code += "  exit /b 1\n";
        code += ")\n\n";
    }
    
    // 各設定項目の元に戻すコードを生成（選択された項目のみ、revertCodeがある場合のみ）
    sections.forEach(section => {
        section.items.forEach(item => {
            if ((item as any).isAdminCheck || (item as any).isHeaderOption || (item as any).isFooterOption || (item as any).isAppInstall) return;
            if ((document.getElementById(item.id) as HTMLInputElement | null)?.checked && item.revertCode) {
                code += "echo.\n";
                code += "echo ========================================\n";
                code += `echo        ${getSettingTitle(item.id, item.title)} (${t('revert')})\n`;
                code += "echo ========================================\n";
                
                // 翻訳された元に戻すコードを取得（なければ元のコードを使用）
                const revertCodeRaw = getSettingRevertCode(item.id, item.revertCode || '');
                if (logEnabled && revertCodeRaw) {
                    // 変数を置換
                    let itemCode = replaceVariables(revertCodeRaw, item);
                    // ログ出力を追加（echo文にログリダイレクトを追加）
                    const codeWithLog = itemCode.replace(/(echo[^\n]*)/g, '$1' + logCode);
                    code += codeWithLog;
                } else {
                    // 変数を置換
                    let itemCode = replaceVariables(revertCodeRaw, item);
                    code += itemCode;
                }
                code += "\n";
            }
        });
    });
    
    // NIC設定の元に戻すコード（DHCPに戻す）
    sections.forEach(section => {
        section.items.forEach(item => {
            if ((item as any).isDynamicList && (item as any).listItemType === 'nic' && (document.getElementById(item.id) as HTMLInputElement | null)?.checked) {
                const revertNICCode = generateRevertNICCode(item.id, logEnabled, logCode);
                if (revertNICCode) {
                    code += "echo.\n";
                    code += "echo ========================================\n";
                    code += `echo        ${getSettingTitle(item.id, item.title)} (${t('revert')})\n`;
                    code += "echo ========================================\n";
                    code += revertNICCode;
                }
            }
        });
    });
    
    code += "\necho.\n";
    code += "echo ========================================\n";
    code += `echo        ${t('batchRevertComplete')}\n`;
    code += "echo ========================================\n";
    if (logEnabled) {
        code += `echo ${t('batchLogFile')}\n`;
    }
    code += "pause\n";
    
    return code;
}

/**
 * NIC設定の元に戻すコードを生成（DHCPに戻す）
 * @param itemId - 項目ID
 * @param logEnabled - ログ有効か
 * @param logCode - ログリダイレクトコード
 * @returns 生成されたコード
 */
function generateRevertNICCode(itemId: string, logEnabled: boolean, logCode: string): string {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    
    try {
        const raw = window.localStorage.getItem('winbatchgen_nic_config_v1');
        if (!raw) return '';
        
        const config: NICConfigStorage = JSON.parse(raw);
        const nics = config[itemId] || [];
        
        if (nics.length === 0) return '';
        
        let code = '';
        nics.forEach((nic: NICConfig, index: number) => {
            code += `:: NIC #${index + 1} (${nic.adapterName}) をDHCPに戻す\n`;
            code += `netsh interface ip set dns "${nic.adapterName}" dhcp\n`;
            code += `netsh interface ip set address "${nic.adapterName}" dhcp\n`;
            code += '\n';
        });
        
        if (logEnabled) {
            // ログ出力を追加（echo文にログリダイレクトを追加）
            code = code.replace(/(echo[^\n]*)/g, '$1' + logCode);
        }
        
        return code;
    } catch (error) {
        console.error('NIC設定の元に戻すコード生成エラー:', error);
        return '';
    }
}

